import { StatusModal } from '@/components/StatusModal';
import { A6PrintModal } from '@/components/vyroba/A6PrintModal';
import { HistoryModal } from '@/components/vyroba/HistoryModal';
import { type CreatePrefill, TabidooDetailModal } from '@/components/vyroba/TabidooDetailModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ColorSwatch } from './ColorField';

type Column = { key: string; label: string; type?: string };
type Row = { tabidoo_id: string; values: Record<string, string> };
type TableData = {
  columns: Column[];
  rows: Row[];
  synced_at: number;
  /** Jen u `serverLimit`: počty přes celou tabulku (řádků je načtená jen část). */
  total?: number;
  matched?: number;
  truncated?: boolean;
  status_counts?: Record<string, number>;
  suspicious?: number;
};

const PAGE_SIZES = [25, 50, 100];

/** Porovná dvě buňky: číselně když obě vypadají jako číslo, jinak česky. Prázdné na konec. */
function compareValues(a: string, b: string): number {
  const ae = a.trim() === '';
  const be = b.trim() === '';
  if (ae || be) return ae === be ? 0 : ae ? 1 : -1;
  const na = Number(a.replace(/\s/g, '').replace(',', '.'));
  const nb = Number(b.replace(/\s/g, '').replace(',', '.'));
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return a.localeCompare(b, 'cs');
}

/**
 * Datum a čas do čitelné podoby. Datum bereme z ISO řetězce tak, jak je (u
 * denního údaje by převod do lokálního času posunul den), čas u datetime
 * naopak převádíme — v databázi je UTC, dílna ho čte v našem čase.
 */
function formatCell(type: string | undefined, value: string): string {
  if (!value) return value;
  if (type === 'date') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
  }
  if (type === 'datetime') {
    const ms = Date.parse(value);
    if (Number.isNaN(ms)) return value;
    const d = new Date(ms);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  }
  return value;
}

/** Rychlofiltr stavu — karta (název nahoře, velké číslo dole), na celou šířku. */
function StatusChip({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 min-w-[110px] flex flex-col items-center justify-between gap-1 rounded-xl border px-2 py-3 min-h-[92px] transition-colors cursor-pointer ${
        active ? 'border-primary ring-2 ring-primary/40' : 'border-border'
      } ${color ?? (active ? 'bg-primary-50' : 'bg-white hover:bg-surface-hover')}`}
    >
      <span className="text-[11px] font-bold uppercase tracking-wide text-center leading-tight text-text-secondary">
        {label}
      </span>
      <span className="text-3xl font-bold text-text">{count}</span>
    </button>
  );
}

/**
 * Generická datová stránka nad zamrzlým zrcadlem Tabidoo tabulky (Tabidoo se
 * nepoužívá, sync je pryč — jde jen o lokální D1 evidenci s editací detailu).
 * Sloupce (přehazovatelné drag&drop), globální hledání, filtr per sloupec,
 * stránkování. `table` je klíč tabulky (viz registr v api/routes/vyroba).
 */
export function TabidooTablePage({
  table: tableKey,
  title,
  subtitle,
  storagePrefix,
  defaultSort,
  createLabel,
  renderTop,
  statusColumn,
  statusColors,
  statusChips,
  suspiciousBanner,
  renderA6Sheet,
  doneBadge,
  serverLimit,
  inlineChoice,
  rowColor,
}: {
  table: string;
  title: string;
  subtitle?: string;
  storagePrefix: string;
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  createLabel?: string; // když je zadáno, zobrazí tlačítko pro založení nového záznamu
  /** Vlastní blok nad tabulkou; dostane akce stránky (založení, přenačtení). */
  renderTop?: (api: {
    /** Otevře zakládání záznamu; `onSaved` se zavolá po úspěšném uložení. */
    create: (prefill?: CreatePrefill, onSaved?: () => void | Promise<void>) => void;
    reload: () => Promise<void>;
    /** Všechny načtené řádky (bez ohledu na filtr). */
    rows: Row[];
  }) => ReactNode;
  statusColumn?: string; // klíč sloupce, podle kterého barvit řádky
  statusColors?: Record<string, string>; // hodnota stavu → třídy pozadí (bg + hover)
  statusChips?: string[]; // rychlofiltry stavů (pořadí) nad tabulkou
  suspiciousBanner?: {
    label: string;
    dateColumn: string; // sloupec s datem vytvoření
    olderThanDays: number;
    excludeStatuses: string[]; // stavy, které za podezřelé nepovažujeme
  };
  renderA6Sheet?: (values: Record<string, string>) => ReactNode; // A6 výrobní list
  /** Ikona před hodnotu sloupce `nameColumn`, když `statusColumn` má hodnotu `statusValue`. */
  doneBadge?: { nameColumn: string; statusValue: string; icon?: string };
  /**
   * Velká tabulka: bez filtru se načte jen tolik nejnovějších záznamů (podle
   * `defaultSort`), hledání a filtry se pak posílají na server přes všechny.
   */
  serverLimit?: number;
  /** Sloupec, jehož hodnota se vybírá tlačítky přímo v řádku (opakovaný klik = zrušit). */
  inlineChoice?: { key: string; options: string[] };
  /** Vlastní barva řádku podle hodnot (třídy bg + hover) — jako `statusColors`. */
  rowColor?: (values: Record<string, string>) => string | undefined;
}) {
  const ORDER_KEY = `${storagePrefix}-col-order`;
  const HIDDEN_KEY = `${storagePrefix}-col-hidden`;
  const WIDTH_KEY = `${storagePrefix}-col-widths`;

  const [table, setTable] = useState<TableData | null>(null);
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [a6Open, setA6Open] = useState(false);
  const [a6AutoPrint, setA6AutoPrint] = useState(false);
  const [copying, setCopying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<CreatePrefill | undefined>();
  // Co udělat po uložení právě zakládaného záznamu (např. vyřídit frontu).
  const afterCreate = useRef<(() => void | Promise<void>) | null>(null);
  // Výchozí 50 řádků; volba se pamatuje pro každou tabulku zvlášť.
  const PAGE_SIZE_KEY = `${storagePrefix}-page-size`;
  const [pageSize, setPageSize] = useState(() => {
    const ulozena = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(ulozena) ? ulozena : 50;
  });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    defaultSort ?? null,
  );
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [suspiciousActive, setSuspiciousActive] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  // Je řádek „podezřelý"? (stav mimo excludeStatuses a starší než N dní.)
  const isSuspicious = useCallback(
    (row: Row) => {
      if (!suspiciousBanner || !statusColumn) return false;
      if (suspiciousBanner.excludeStatuses.includes(row.values[statusColumn] ?? '')) return false;
      const ds = row.values[suspiciousBanner.dateColumn];
      const t = ds ? new Date(ds).getTime() : Number.NaN;
      if (Number.isNaN(t)) return false;
      return (Date.now() - t) / 86_400_000 >= suspiciousBanner.olderThanDays;
    },
    [suspiciousBanner, statusColumn],
  );

  // Počty pro rychlofiltry (z celého datasetu, ne z filtrovaného).
  const statusCounts = useMemo(() => {
    if (table?.status_counts) return table.status_counts;
    const m: Record<string, number> = {};
    if (statusColumn)
      for (const r of table?.rows ?? [])
        m[r.values[statusColumn] ?? ''] = (m[r.values[statusColumn] ?? ''] ?? 0) + 1;
    return m;
  }, [table, statusColumn]);
  const suspiciousCount = useMemo(
    () => table?.suspicious ?? (table?.rows ?? []).filter(isSuspicious).length,
    [table, isSuspicious],
  );

  // U `serverLimit` jdou hledání a filtry na server (s krátkým zpožděním při
  // psaní); jinak se načte celá tabulka a filtruje se tady.
  const [serverQuery, setServerQuery] = useState('');
  useEffect(() => {
    if (!serverLimit) return;
    const p = new URLSearchParams({ limit: String(serverLimit) });
    if (defaultSort) p.set('order', defaultSort.key);
    if (search.trim()) p.set('q', search.trim());
    const f = Object.fromEntries(Object.entries(colFilters).filter(([, v]) => v.trim()));
    if (Object.keys(f).length) p.set('f', JSON.stringify(f));
    if (statusColumn) p.set('sc', statusColumn);
    if (statusColumn && statusFilter !== null) p.set('stav', statusFilter);
    if (suspiciousBanner && statusColumn) {
      p.set(
        'pod',
        JSON.stringify({
          statusCol: statusColumn,
          dateCol: suspiciousBanner.dateColumn,
          days: suspiciousBanner.olderThanDays,
          exclude: suspiciousBanner.excludeStatuses,
        }),
      );
      if (suspiciousActive) p.set('podezrele', '1');
    }
    const t = setTimeout(() => setServerQuery(`?${p.toString()}`), 350);
    return () => clearTimeout(t);
  }, [
    serverLimit,
    defaultSort,
    search,
    colFilters,
    statusColumn,
    statusFilter,
    suspiciousBanner,
    suspiciousActive,
  ]);

  const load = useCallback(async () => {
    if (serverLimit && !serverQuery) return;
    try {
      const d = await apiFetch<TableData>(`/api/vyroba/tabidoo/${tableKey}${serverQuery}`);
      setTable(d);
    } catch (e: unknown) {
      setTable({ columns: [], rows: [], synced_at: 0 });
      setToast({ msg: e instanceof ApiError ? e.message : 'Chyba načítání', kind: 'error' });
    }
  }, [tableKey, serverLimit, serverQuery]);

  useEffect(() => {
    load();
  }, [load]);

  // Výběr hodnoty přímo v řádku — hned se ukáže, při chybě se vrátí zpět.
  const saveInline = async (id: string, key: string, value: string) => {
    const patchRow = (v: string) =>
      setTable((t) =>
        t
          ? {
              ...t,
              rows: t.rows.map((r) =>
                r.tabidoo_id === id ? { ...r, values: { ...r.values, [key]: v } } : r,
              ),
            }
          : t,
      );
    const prev = table?.rows.find((r) => r.tabidoo_id === id)?.values[key] ?? '';
    patchRow(value);
    try {
      await apiFetch(`/api/vyroba/tabidoo/${tableKey}/${id}`, {
        method: 'PATCH',
        body: { fields: { [key]: value || null } },
      });
    } catch (e: unknown) {
      patchRow(prev);
      setToast({ msg: e instanceof ApiError ? e.message : 'Uložení selhalo', kind: 'error' });
    }
  };

  // „Uložit a tisknout" v detailu/zakládání — vyber uložený řádek, otevři A6
  // a rovnou vyvolej tiskový dialog. Jen na stránkách, které A6 list mají.
  const printAfterSave = useMemo(
    () =>
      renderA6Sheet
        ? (id: string) => {
            setSelectedId(id);
            setA6AutoPrint(true);
            setA6Open(true);
          }
        : undefined,
    [renderA6Sheet],
  );

  // Skryté sloupce z localStorage.
  // biome-ignore lint/correctness/useExhaustiveDependencies: HIDDEN_KEY je stabilní per stránka
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_KEY);
      if (raw) setHidden(new Set(JSON.parse(raw)));
    } catch {
      /* ignore */
    }
  }, []);

  // Šířky sloupců z localStorage.
  // biome-ignore lint/correctness/useExhaustiveDependencies: WIDTH_KEY je stabilní per stránka
  useEffect(() => {
    try {
      const raw = localStorage.getItem(WIDTH_KEY);
      if (raw) setColWidths(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  // Pořadí sloupců: z localStorage (co existuje). Nový sloupec, který uložené
  // pořadí ještě nezná, vložíme na jeho místo podle schématu (např. Náhled
  // zipu jde doleva), ne na konec.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ORDER_KEY je stabilní per stránka
  useEffect(() => {
    if (!table) return;
    const keys = table.columns.map((c) => c.key);
    let stored: string[] = [];
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    const order = stored.filter((k) => keys.includes(k));
    keys.forEach((k, i) => {
      if (!order.includes(k)) order.splice(Math.min(i, order.length), 0, k);
    });
    setColumnOrder(order);
  }, [table]);

  // Změna filtru/hledání/velikosti stránky/řazení → zpět na první stránku.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset na filtry/velikost/řazení
  useEffect(() => {
    setPage(1);
  }, [search, colFilters, pageSize, sort, statusFilter, suspiciousActive]);

  // Klik na hlavičku: asc → desc → bez řazení.
  function toggleSort(key: string) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      return prev.dir === 'asc' ? { key, dir: 'desc' } : null;
    });
  }

  async function deleteSelected() {
    if (!selectedId) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/vyroba/tabidoo/${tableKey}/${selectedId}`, { method: 'DELETE' });
      setToast({ msg: 'Záznam smazán.', kind: 'success' });
      setSelectedId(null);
      setConfirmDelete(false);
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Smazání selhalo', kind: 'error' });
    } finally {
      setDeleting(false);
    }
  }

  const colByKey = useMemo(() => {
    const m = new Map<string, Column>();
    for (const c of table?.columns ?? []) m.set(c.key, c);
    return m;
  }, [table]);

  const cols = useMemo(
    () => columnOrder.map((k) => colByKey.get(k)).filter((c): c is Column => !!c),
    [columnOrder, colByKey],
  );
  const visibleCols = useMemo(() => cols.filter((c) => !hidden.has(c.key)), [cols, hidden]);

  function updateHidden(next: Set<string>) {
    setHidden(next);
    try {
      localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next]));
    } catch {
      /* ignore */
    }
  }
  function toggleColumn(key: string) {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    updateHidden(next);
  }

  function dropOnColumn(targetKey: string) {
    if (!dragKey || dragKey === targetKey) {
      setDragKey(null);
      return;
    }
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== dragKey);
      const idx = next.indexOf(targetKey);
      next.splice(idx < 0 ? next.length : idx, 0, dragKey);
      try {
        localStorage.setItem(ORDER_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    setDragKey(null);
  }

  // Tažení pravého okraje hlavičky → změna šířky sloupce (uloží se do localStorage).
  function startResize(e: ReactMouseEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    setResizingKey(key);
    const startX = e.clientX;
    const th = (e.currentTarget as HTMLElement).parentElement;
    const startW = colWidths[key] ?? th?.offsetWidth ?? 120;
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(60, Math.round(startW + (ev.clientX - startX)));
      setColWidths((p) => ({ ...p, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setResizingKey(null);
      setColWidths((p) => {
        try {
          localStorage.setItem(WIDTH_KEY, JSON.stringify(p));
        } catch {
          /* ignore */
        }
        return p;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  // Filtrování: globální hledání + filtr per sloupec.
  const filtered = useMemo(() => {
    if (!table) return [];
    const q = search.trim().toLowerCase();
    const active = Object.entries(colFilters).filter(([, v]) => v.trim());
    return table.rows.filter((row) => {
      if (statusFilter && statusColumn && (row.values[statusColumn] ?? '') !== statusFilter) {
        return false;
      }
      if (suspiciousActive && !isSuspicious(row)) return false;
      const shown = (key: string) => {
        const raw = row.values[key] ?? '';
        const col = table.columns.find((c) => c.key === key);
        return `${raw} ${formatCell(col?.type, raw)}`.toLowerCase();
      };
      if (q && !table.columns.some((c) => shown(c.key).includes(q))) return false;
      for (const [key, val] of active) {
        if (!shown(key).includes(val.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [table, search, colFilters, statusFilter, statusColumn, suspiciousActive, isSuspicious]);

  // Seřazení (klik na hlavičku). Bez řazení = pořadí ze serveru.
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, dir } = sort;
    const arr = [...filtered].sort((ra, rb) =>
      compareValues(ra.values[key] ?? '', rb.values[key] ?? ''),
    );
    if (dir === 'desc') arr.reverse();
    return arr;
  }, [filtered, sort]);

  /**
   * Kopie vybraného záznamu — otevře zakládací modal s předvyplněnými poli.
   * Vynecháme readonly pole (kódy a dopočítané hodnoty si systém doplní sám)
   * a propojení převedeme na id, protože ve formuláři se drží jako id.
   */
  async function kopirovat() {
    if (!selectedId) return;
    setCopying(true);
    try {
      const d = await apiFetch<{
        formFields: { name: string; type: string; readonly?: boolean }[];
        values: Record<string, unknown>;
      }>(`/api/vyroba/tabidoo/${tableKey}/${selectedId}`);
      // Readonly pole se nekopírují — kódy si systém přidělí sám.
      const readonly = new Set(d.formFields.filter((f) => f.readonly).map((f) => f.name));
      const fields: Record<string, unknown> = {};
      for (const [name, value] of Object.entries(d.values)) {
        if (readonly.has(name) || value == null || value === '') continue;
        if (Array.isArray(value)) {
          // Přílohy a náhledy (obrázek látky) přebíráme beze změny.
          fields[name] = value;
          continue;
        }
        if (typeof value === 'object') {
          const id = (value as { id?: unknown }).id;
          if (typeof id === 'string') fields[name] = id;
          continue;
        }
        fields[name] = value;
      }
      setCreatePrefill({ fields });
      setCreating(true);
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Kopii se nepodařilo připravit',
        kind: 'error',
      });
    } finally {
      setCopying(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
  const hasColFilters = Object.values(colFilters).some((v) => v.trim());

  return (
    <>
      {renderTop?.({
        create: (prefill, onSaved) => {
          setCreatePrefill(prefill);
          afterCreate.current = onSaved ?? null;
          setCreating(true);
        },
        reload: load,
        rows: table?.rows ?? [],
      })}

      <div className="mb-5">
        <h1 className="text-2xl font-bold text-text">{title}</h1>
        {subtitle && <p className="text-sm text-text-secondary mt-0.5">{subtitle}</p>}
      </div>

      {/* Ovládací lišta */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat ve všech sloupcích…"
            className="w-full bg-white border border-border rounded-lg pl-9 pr-3 py-2.5 text-sm text-text shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
        <span className="text-sm text-text-secondary">
          Zobrazeno <strong className="text-text">{filtered.length}</strong>
          {table?.total !== undefined
            ? ` z ${table.total}`
            : table && filtered.length !== table.rows.length
              ? ` z ${table.rows.length}`
              : ''}{' '}
          řádků
          {table?.truncated &&
            (table.matched !== undefined && table.matched < (table.total ?? 0)
              ? ` (shod ${table.matched}, ukázány první)`
              : ' — nejnovější, starší najde hledání nebo filtr')}
        </span>
        {table && table.synced_at > 0 && (
          <span className="text-xs text-text-muted">Sync: {fmtDateTime(table.synced_at)}</span>
        )}
        {hasColFilters && (
          <button
            type="button"
            onClick={() => setColFilters({})}
            className="text-xs font-semibold text-primary hover:underline cursor-pointer"
          >
            Zrušit filtry
          </button>
        )}
      </div>

      {/* Rychlofiltry stavů */}
      {statusChips && statusChips.length > 0 && table && (
        <div className="flex flex-wrap gap-2 mb-3">
          <StatusChip
            label="Vše"
            count={table.rows.length}
            active={!statusFilter && !suspiciousActive}
            onClick={() => {
              setStatusFilter(null);
              setSuspiciousActive(false);
            }}
          />
          {statusChips.map((st) => (
            <StatusChip
              key={st}
              label={st}
              count={statusCounts[st] ?? 0}
              color={statusColors?.[st]}
              active={statusFilter === st && !suspiciousActive}
              onClick={() => {
                setStatusFilter(st);
                setSuspiciousActive(false);
              }}
            />
          ))}
        </div>
      )}

      {/* Banner podezřelých zakázek */}
      {suspiciousBanner && table && (
        <button
          type="button"
          onClick={() => {
            setSuspiciousActive((a) => !a);
            setStatusFilter(null);
          }}
          className={`w-full mb-4 rounded-xl border px-5 py-3 text-left transition-colors cursor-pointer ${
            suspiciousActive
              ? 'bg-danger-light border-danger ring-2 ring-danger/30'
              : 'bg-danger-light border-danger/20 hover:border-danger/50'
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-bold text-danger uppercase tracking-wide">
              {suspiciousBanner.label}
            </span>
            <span className="text-2xl font-bold text-danger">{suspiciousCount}</span>
          </div>
        </button>
      )}

      {table == null ? (
        <p className="text-sm text-text-muted">Načítám…</p>
      ) : cols.length === 0 ? (
        <p className="text-sm text-text-muted">Žádná data.</p>
      ) : (
        <>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              {createLabel && (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex items-center gap-1 bg-action hover:bg-action-dark text-white rounded-lg px-6 py-3 text-base font-bold shadow-sm cursor-pointer transition-colors"
                >
                  + {createLabel}
                </button>
              )}
              <button
                type="button"
                disabled={!selectedId || copying}
                onClick={kopirovat}
                aria-label="Vytvořit kopii"
                title={
                  selectedId ? 'Založit nový záznam podle vybraného' : 'Nejdřív klikni na řádek'
                }
                className="flex items-center justify-center bg-white border border-border rounded-lg p-2 text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="9" y="9" width="12" height="12" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
              <button
                type="button"
                disabled={!selectedId}
                onClick={() => setHistoryOpen(true)}
                aria-label="Historie změn"
                title={selectedId ? 'Historie změn vybraného záznamu' : 'Nejdřív klikni na řádek'}
                className="flex items-center justify-center bg-white border border-border rounded-lg p-2 text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                  <path d="M12 7v5l3 3" />
                </svg>
              </button>
              <button
                type="button"
                disabled={!selectedId}
                onClick={() => setConfirmDelete(true)}
                aria-label="Smazat zakázku"
                title={selectedId ? 'Smazat vybranou zakázku' : 'Nejdřív klikni na řádek'}
                className="flex items-center justify-center bg-white border border-border rounded-lg p-2 text-text-secondary hover:bg-danger-light hover:text-danger hover:border-danger/40 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />
                </svg>
              </button>
              {renderA6Sheet && (
                <button
                  type="button"
                  disabled={!selectedId}
                  onClick={() => {
                    setA6AutoPrint(false);
                    setA6Open(true);
                  }}
                  title={selectedId ? 'A6 výrobní list k tisku' : 'Nejdřív klikni na řádek'}
                  className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-2 text-sm font-semibold text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />
                  </svg>
                  A6 papírek
                </button>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setSettingsOpen((o) => !o)}
                className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-1.5 text-sm font-semibold text-text-secondary hover:bg-surface-hover cursor-pointer transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Vybrat sloupce
              </button>
              {settingsOpen && (
                <>
                  <button
                    type="button"
                    aria-label="Zavřít"
                    onClick={() => setSettingsOpen(false)}
                    className="fixed inset-0 z-40 cursor-default"
                  />
                  <div className="absolute right-0 mt-2 z-50 w-72 max-h-[70vh] overflow-y-auto bg-surface border border-border rounded-lg shadow-modal p-2">
                    <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-border-light">
                      <span className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                        Výběr sloupců
                      </span>
                      <span className="flex gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => updateHidden(new Set())}
                          className="text-primary hover:underline cursor-pointer"
                        >
                          Vše
                        </button>
                        <button
                          type="button"
                          onClick={() => updateHidden(new Set(cols.map((c) => c.key)))}
                          className="text-primary hover:underline cursor-pointer"
                        >
                          Nic
                        </button>
                      </span>
                    </div>
                    {cols.map((c) => (
                      <label
                        key={c.key}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer text-sm text-text"
                      >
                        <input
                          type="checkbox"
                          checked={!hidden.has(c.key)}
                          onChange={() => toggleColumn(c.key)}
                          className="shrink-0"
                        />
                        <span className="truncate">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="overflow-x-auto border border-border rounded-lg bg-surface shadow-card">
            <table className="text-sm border-collapse">
              <thead>
                <tr className="bg-primary">
                  {visibleCols.map((c) => (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: klik řadí, doplňkové k UI (interní nástroj)
                    <th
                      key={c.key}
                      draggable={resizingKey === null}
                      onDragStart={(e) => {
                        if (resizingKey) {
                          e.preventDefault();
                          return;
                        }
                        setDragKey(c.key);
                      }}
                      onDragEnd={() => setDragKey(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropOnColumn(c.key)}
                      onClick={() => toggleSort(c.key)}
                      title="Klikni pro řazení, táhni pro přesun sloupce, okraj pro změnu šířky"
                      style={colWidths[c.key] ? { width: colWidths[c.key] } : undefined}
                      className={`relative px-3 py-2.5 text-left text-xs font-bold text-white border-b-2 border-primary-dark cursor-pointer select-none whitespace-nowrap overflow-hidden text-ellipsis ${
                        dragKey === c.key ? 'opacity-40' : ''
                      }`}
                    >
                      {c.label}
                      {sort?.key === c.key && (
                        <span className="ml-1">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {/* Úchyt pro změnu šířky na pravém okraji. */}
                      <span
                        onMouseDown={(e) => startResize(e, c.key)}
                        title="Táhni pro změnu šířky"
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-white/40"
                      />
                    </th>
                  ))}
                </tr>
                <tr className="bg-white">
                  {visibleCols.map((c) => (
                    <th
                      key={c.key}
                      style={colWidths[c.key] ? { width: colWidths[c.key] } : undefined}
                      className="px-2 py-2 border-b-2 border-border align-top"
                    >
                      <input
                        value={colFilters[c.key] ?? ''}
                        onChange={(e) => setColFilters((p) => ({ ...p, [c.key]: e.target.value }))}
                        placeholder="Filtr"
                        className="w-full min-w-0 border border-border rounded px-2 py-1 text-xs font-normal text-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      className="px-3 py-6 text-center text-text-muted whitespace-nowrap"
                    >
                      Nic neodpovídá filtru.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, i) => {
                    // Barva řádku podle stavu (když je nastaveno); jinak zebra.
                    const statusBg =
                      rowColor?.(row.values) ??
                      (statusColumn ? statusColors?.[row.values[statusColumn] ?? ''] : undefined);
                    const selected = selectedId === row.tabidoo_id;
                    // Výběr přebije barvu stavu i zebru (světle modrá jako v Tabidoo).
                    const rowBg = selected
                      ? 'bg-[#dbe4ff]'
                      : (statusBg ??
                        `${i % 2 === 1 ? 'bg-primary-25' : 'bg-white'} hover:bg-primary-50`);
                    return (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: řádek tabulky, klik/dvojklik je doplňkový k viditelnému kurzoru (interní nástroj)
                      <tr
                        key={row.tabidoo_id}
                        onClick={() => setSelectedId(row.tabidoo_id)}
                        onDoubleClick={() => setDetailId(row.tabidoo_id)}
                        title="Klikni pro výběr, dvojklik pro detail"
                        className={`${rowBg} transition-colors cursor-pointer`}
                      >
                        {visibleCols.map((c) => (
                          <td
                            key={c.key}
                            style={
                              colWidths[c.key]
                                ? { width: colWidths[c.key], maxWidth: colWidths[c.key] }
                                : undefined
                            }
                            className={`px-3 py-2 text-text whitespace-nowrap ${
                              colWidths[c.key] ? 'overflow-hidden text-ellipsis' : ''
                            }`}
                          >
                            {inlineChoice?.key === c.key ? (
                              <span className="inline-flex gap-1">
                                {inlineChoice.options.map((o) => {
                                  const active = row.values[c.key] === o;
                                  return (
                                    <button
                                      key={o}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Klik na vybranou hodnotu ji zruší; druhý klik dvojkliku
                                        // (detail > 1) se ignoruje, jinak by hodnotu hned smazal.
                                        if (e.detail > 1) return;
                                        void saveInline(row.tabidoo_id, c.key, active ? '' : o);
                                      }}
                                      onDoubleClick={(e) => e.stopPropagation()}
                                      className={`px-2 py-0.5 rounded border text-xs tabular-nums cursor-pointer ${
                                        active
                                          ? 'bg-primary text-white border-primary font-semibold'
                                          : 'bg-surface text-text-secondary border-border hover:border-primary'
                                      }`}
                                    >
                                      {o}
                                    </button>
                                  );
                                })}
                              </span>
                            ) : c.type === 'color' ? (
                              <ColorSwatch value={row.values[c.key] ?? ''} />
                            ) : c.type === 'picture' ? (
                              row.values[c.key] ? (
                                <img
                                  src={row.values[c.key]}
                                  alt=""
                                  loading="lazy"
                                  // Fotku produktu neořezáváme (kalhoty na výšku by
                                  // v kolečku přišly o nohavice), vzorek látky ano.
                                  className={
                                    c.key === '__foto'
                                      ? 'h-10 w-10 object-contain rounded border border-border bg-white'
                                      : 'h-10 w-10 object-cover rounded-full border border-border'
                                  }
                                />
                              ) : null
                            ) : (
                              <>
                                {doneBadge &&
                                c.key === doneBadge.nameColumn &&
                                statusColumn &&
                                row.values[statusColumn] === doneBadge.statusValue
                                  ? `${doneBadge.icon ?? '✅'} `
                                  : null}
                                {formatCell(c.type, row.values[c.key] ?? '')}
                              </>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Stránkování */}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              Řádků na stránku:
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  localStorage.setItem(PAGE_SIZE_KEY, e.target.value);
                }}
                className="border border-border rounded-lg px-2 py-1.5 text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                className="border border-border rounded-lg px-3 py-1.5 text-sm font-semibold text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                ‹ Předchozí
              </button>
              <span className="text-sm text-text-secondary">
                Stránka <strong className="text-text">{safePage}</strong> z {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="border border-border rounded-lg px-3 py-1.5 text-sm font-semibold text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                Další ›
              </button>
            </div>
          </div>
        </>
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
      {detailId && (
        <TabidooDetailModal
          table={tableKey}
          recordId={detailId}
          onClose={() => setDetailId(null)}
          onSaved={load}
          onSavedAndPrint={printAfterSave}
        />
      )}
      {creating && (
        <TabidooDetailModal
          table={tableKey}
          createTitle={createLabel}
          prefill={createPrefill}
          onClose={() => {
            setCreating(false);
            setCreatePrefill(undefined);
            afterCreate.current = null;
          }}
          onSaved={async () => {
            await load();
            const done = afterCreate.current;
            afterCreate.current = null;
            await done?.();
          }}
          onSavedAndPrint={printAfterSave}
        />
      )}
      {historyOpen && selectedId && (
        <HistoryModal
          table={tableKey}
          recordId={selectedId}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {a6Open &&
        renderA6Sheet &&
        (() => {
          const row = table?.rows.find((r) => r.tabidoo_id === selectedId);
          if (!row) return null;
          return (
            <A6PrintModal
              autoPrint={a6AutoPrint}
              onClose={() => {
                setA6Open(false);
                setA6AutoPrint(false);
              }}
            >
              {renderA6Sheet(row.values)}
            </A6PrintModal>
          );
        })()}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Zavřít"
            onClick={() => !deleting && setConfirmDelete(false)}
            className="fixed inset-0 bg-black/50 cursor-default"
          />
          <div className="relative w-full max-w-sm bg-surface rounded-2xl shadow-modal reveal p-6">
            <h2 className="text-lg font-bold text-text mb-1">Smazat záznam?</h2>
            <p className="text-sm text-text-secondary mb-5">
              Tato akce je nevratná — záznam se smaže z Tabidoo i z našeho zrcadla.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer disabled:opacity-50"
              >
                Zrušit
              </button>
              <button
                type="button"
                onClick={deleteSelected}
                disabled={deleting}
                className="bg-danger hover:bg-danger/90 disabled:opacity-50 text-white text-sm font-bold rounded-[5px] px-5 py-2 cursor-pointer transition-colors"
              >
                {deleting ? 'Mažu…' : 'Smazat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
